import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TomatoTimer from '../components/TomatoTimer';
import TaskList from '../components/TaskList';
import { motion } from 'framer-motion';
import { 
  LightBulbIcon, 
  InformationCircleIcon, 
  ClipboardDocumentListIcon,
  ArrowLongRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function HomePage() {
  const [showTasks, setShowTasks] = useState(false);
  const navigate = useNavigate();
  
  // Array of tips for the Pomodoro technique
  const tips = [
    "A 'pomodoro' is one complete unit: 25 minutes of focused work plus a 5-minute break.",
    "Focus fully on one task during each Pomodoro focus period for maximum productivity.",
    "Use short breaks to stretch, breathe deeply, or grab a glass of water.",
    "Don't check email or messages during your focus sessions - note them down to handle later.",
    "If a task takes more than 5 Pomodoros, break it down into smaller, manageable tasks.",
    "Keep a notepad nearby to jot down distracting thoughts that pop up during a Pomodoro.",
    "Physical activity during breaks improves blood flow and focus for your next session.",
    "The technique helps reduce mental fatigue by forcing regular breaks.",
    "If you finish early, use the remaining time to review or improve your work.",
    "Tell others you're in a focus session to reduce interruptions.",
    "Adjust the timings to suit your personal concentration rhythm."
  ];
  
  // Select a random tip
  const randomTip = tips[Math.floor(Math.random() * tips.length)];
  
  // Handler for scrolling to top of About page
  const handleAboutClick = () => {
    navigate('/about');
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  
  return (
    <div className="max-w-5xl mx-auto px-4 py-6" id="main-content">
      {/* Introduction Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <h1 className="text-4xl md:text-5xl font-display font-bold text-center text-tomato-700 mb-3">Focus Better with Pomodoro</h1>
        <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto">
          Simple, powerful time management to boost your productivity and concentration.
        </p>
      </motion.div>
      
      {/* Main Timer Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white bg-opacity-90 rounded-2xl shadow-xl p-8 mb-8 overflow-hidden"
      >
        <TomatoTimer />
        
        <div className="mt-10 flex justify-center">
          <button
            onClick={() => setShowTasks(!showTasks)}
            className="group btn-outline text-lg px-6 py-3 flex items-center space-x-2"
          >
            <ClipboardDocumentListIcon className="w-6 h-6" />
            <span>{showTasks ? 'Hide Task List' : 'Show Task List'}</span>
            <ArrowLongRightIcon className="w-6 h-6 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </motion.div>
      
      {/* Task List Section */}
      {showTasks && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white bg-opacity-90 rounded-2xl shadow-xl p-8 overflow-hidden mb-8"
        >
          <TaskList />
        </motion.div>
      )}
      
      {/* Tip Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-tomato-50 rounded-2xl p-8 shadow-lg mb-8"
      >
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 bg-white p-3 rounded-full shadow-md">
            <LightBulbIcon className="w-8 h-8 text-tomato-600" />
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold text-tomato-700 mb-2">Tip:</h2>
            <p className="text-lg text-gray-700">
              {randomTip}
            </p>
          </div>
        </div>
      </motion.div>
      
      {/* How It Works Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-white bg-opacity-90 rounded-2xl p-8 shadow-xl"
      >
        <div className="flex items-start space-x-4 mb-6">
          <div className="flex-shrink-0 bg-tomato-50 p-3 rounded-full shadow-md">
            <InformationCircleIcon className="w-8 h-8 text-tomato-600" />
          </div>
          <h2 className="text-2xl font-display font-bold text-tomato-700">How It Works</h2>
        </div>
        
        <div className="grid md:grid-cols-4 gap-6 mt-6">
          <div className="bg-tomato-50 p-6 rounded-xl shadow-md transform transition-transform hover:scale-105">
            <div className="w-12 h-12 bg-tomato-600 rounded-full flex items-center justify-center text-white font-bold mb-4">1</div>
            <h3 className="text-xl font-bold mb-2 text-tomato-700">1 × Pomodoro</h3>
            <p className="text-lg">25 minutes focus + 5 minute break = one complete pomodoro.</p>
          </div>
          
          <div className="bg-tomato-50 p-6 rounded-xl shadow-md transform transition-transform hover:scale-105">
            <div className="w-12 h-12 bg-sunny-500 rounded-full flex items-center justify-center text-white font-bold mb-4">2</div>
            <h3 className="text-xl font-bold mb-2 text-amber-700">4 × Pomodori</h3>
            <p className="text-lg">Complete four pomodori, then take a longer break (10-45 minutes).</p>
          </div>
          
          <div className="bg-tomato-50 p-6 rounded-xl shadow-md transform transition-transform hover:scale-105">
            <div className="w-12 h-12 bg-stem-500 rounded-full flex items-center justify-center text-white font-bold mb-4">3</div>
            <h3 className="text-xl font-bold mb-2 text-green-700">Stay Focused</h3>
            <p className="text-lg">Avoid distractions during focus periods. Note interruptions to handle later.</p>
          </div>
          
          <div className="bg-tomato-50 p-6 rounded-xl shadow-md transform transition-transform hover:scale-105">
            <div className="w-12 h-12 bg-tomato-600 rounded-full flex items-center justify-center text-white font-bold mb-4">4</div>
            <h3 className="text-xl font-bold mb-2 text-tomato-700">Repeat Until Done</h3>
            <p className="text-lg">Continue this cycle until you've completed your task.</p>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <button 
            onClick={handleAboutClick}
            className="text-tomato-600 hover:text-tomato-700 font-bold text-lg flex items-center justify-center mx-auto focus:outline-none focus:underline"
          >
            Learn more about the Pomodoro Technique 
            <ArrowLongRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </motion.div>
    </div>
  );
}
